import { EventEmitter, Injectable, Output } from '@angular/core';
import { SysNotification } from '../models/sysnotification';
import { Observable, Subject } from 'rxjs';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { SwPush } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import { LocalService } from './local.service';

@Injectable({
  providedIn: 'root'
})
export class SysnotificationService {
  @Output() activeSessionEmitter: EventEmitter<any> = new EventEmitter();
  private notificationSocket: Socket;
  private _subject = new Subject<SysNotification>();
  private messageBeep = new Audio('../../assets/audio/message-beep.mp3');
  public companyId: number;
  public readonly publicVapid = environment.vapidKey;

  constructor(
    private auth: AuthService,
    private swPush: SwPush,
    private http: HttpClient,
    private localService: LocalService,
  ) { this.initSettings() }

  /**
   * socketConfig
   * return setting object
   */
  private get socketConfig(): SocketIoConfig {
    return {
      url: `${environment.socketsURL}/notifications`,
      options: {
        transports: ['websocket'],
        reconnection: true,
        autoConnect: true
      }
    }
  }

  public get activeAudioStatus(): any {
    return this.localService.getJsonValue('userData').activeAudio;
  }

  public get activeNotifications(): number {
    return this.localService.getJsonValue('userData').activeNotifications;
  }

  /**
   * socketInit
   * connect to company notification socket and enable the audio listener.
   */
  private socketInit(): void {
    this.notificationSocket = new Socket(this.socketConfig);
    this.companyId = this.auth.user.company.id;
    this.notificationSocket.on('connect', () => {
      this.emit('joinRoom', `${this.auth.user.id}`);
    });
    this.listen('audio').subscribe(() => { this.playMessageBeep(); });
  }

  /**
   * emit
   * emit socket event
   * @param eventName
   */
  emit(eventName: string, message: any) {
    if (this.notificationSocket) this.notificationSocket.emit(eventName, message);
  }

  /**
   * listen
   * listen socket event
   * @param event
   */
  listen(event: string) {
    if (this.notificationSocket) {
      return new Observable((subscriber) => {
        this.notificationSocket.on(event, (data) => { subscriber.next(data); });
      });
    }
  }

  /**
   * initSettingss
   * Validate if the user has audio notifications enabled,
   * and also initialize the subscription with push notifications.
   */
  public initSettings() {
    if (this.localService.getJsonValue('userData').activeAudio === undefined) {
      this.auth.user.activeAudio = true;
      this.localService.setJsonValue('userData', this.auth.user);
    }
    this.socketInit();
    this.initPushNotification();
    this.initNotificationClickListener();
  }

  /**
   * initNotificationClickListener
   * Al hacer clic en una notificación push del navegador, navega a conversaciones.
   */
  private initNotificationClickListener(): void {
    this.swPush.notificationClicks.subscribe(({ action, notification }) => {
      window.open(`${window.location.origin}/admin/conversations`, '_self');
    });
  }

  /**
   * playMessageBeep
   * emit the sound notification when contact send new message
   */
  playMessageBeep() {
    if (this.localService.getJsonValue('userData').activeAudio) {
      this.messageBeep.preload = "auto";
      this.messageBeep.autoplay = true;
      this.messageBeep.play().catch(error => { this.requestAutoplayPermission(error); });
    }
  }

  /**
   * requestAutoplayPermission
   * Display an alert notifying that audio notifications need to be enabled.
   */
  requestAutoplayPermission(error) {
    console.error(error);
  }

  /**
   * initPushNotification
   * Initiate the subscription and generate the push token for the user.
   */
  initPushNotification() {
    this.localService.removeJsonValue('authToken');
    this.swPush.requestSubscription({
      serverPublicKey: this.publicVapid
    }).then(sub => {
      const token = JSON.parse(JSON.stringify(sub));
      if (token && token.keys) {
        this.localService.setJsonValue('authToken', token.keys.auth);
        this.addPushToken({ auth: token.keys.auth, token }).subscribe();
      }
    }).catch(error => { console.error('Error requesting push notification subscription:', error); });
  }

  /**
   * addPushToken
   * Event that adds the user token to the database.
   * @param token
   * @returns Observable<any>
   */
  addPushToken({ auth, token }: { auth: string, token: string }): Observable<any> {
    const sendURL = `${environment.baseServerURL}/security/user/pushToken`;
    const pushToken = {
      user_id: this.auth.user.id,
      auth: auth,
      pushToken: JSON.stringify(token),
      activeSession: 1
    }
    return this.http.post(sendURL, pushToken, { withCredentials: true });
  }

  /**
   * changeNotificationStatus
   * Change the push notification status for the user,
   * which disables the display of push notifications.
   * @param { userId, status }
   * @returns
   */
  changeNotificationStatus({ userId, status }: { userId: number, status: number }): Observable<any> {
    const sendURL = `${environment.baseServerURL}/security/user/changeNotificationStatus/${userId}`;
    this.auth.user.activeNotifications = status;
    this.localService.setJsonValue('userData', this.auth.user);
    return this.http.patch(sendURL, { status: status }, { withCredentials: true });
  }

  /**
   * changeActiveSession   *
   * @param { auth, status }
   * @returns
   */
  public changeActiveSession({ auth, status }: { auth: string, status: number }): Observable<any> {
    const sendURL = `${environment.baseServerURL}/security/user/changeActiveSession/${auth}`;
    return this.http.patch(sendURL, { status }, { withCredentials: true });
  }

  /**
   * setFocused
   * Disable the display of push notifications if the user is located in the active tab.
   * @param { userId, status }
   */
  setFocused(status: number): void {
    if (this.auth.user.activeNotifications === 1)
      this.emit('window_focused', { auth: this.localService.getJsonValue('authToken'), status });
  }

  /**
   * changeAudioStatus
   * Change the status of audio notifications.
   * @param status
   */
  changeAudioStatus(status) {
    this.auth.user.activeAudio = status;
    this.localService.setJsonValue('userData', this.auth.user);
  }

  /**
   * getObservable : ⚠️ Deprecated
   * return subject to Trobbit notifications
   */
  getObservable(): Observable<SysNotification> {
    return this._subject.asObservable();
  }

}
